import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const asyncStatus = {
  idle: 'idle',
  loading: 'loading',
  success: 'success',
  rejected: 'rejected',
};
const defaultAxiosConfig = {
  method: 'get'
};
const defaultConfig = {
  manual: false,
  cancelRequestOnUnmount: true
};

/**
 *
 * @param {(string|Function)} [urlOrFn=""] - The server URL that will be used for the request.
 * @param {Array<any>} [watchDeps=[]]
 * - Array of dependencies to watch and re-execute axios call when any of them is updated.
 * @param {Object} axiosConfig - Axios config for making request.
 * @param {Object} config - useAxios config object.
 * @param {boolean} [config.manual=false]
 * - Flag that makes axios calls to run (or not) automatically.
 * @returns {Object} Response object.
 */
export const useAxios = (urlOrFn = '', watchDeps = [], ...restArgs) => {
  const [response, setResponse] = useState({
    data: null,
    error: null,
    status: asyncStatus.idle,
  });
  const [axiosConfig] = useState(() => ({
    ...defaultAxiosConfig,
    ...(restArgs[0] || {})
  }));
  const [config] = useState(() => ({
    ...defaultConfig,
    ...(restArgs[1] || {})
  }));

  const url = useMemo(() => {
    if (typeof urlOrFn === 'string') {
      return urlOrFn;
    }

    const callbackUrl = String(urlOrFn());

    if (/undefined/.test(callbackUrl)) {
      return undefined;
    }

    return callbackUrl;
  }, [urlOrFn, ...watchDeps]);

  const exec = useCallback((callbackOrObj) => {
    if (typeof url === 'undefined') {
      return;
    }

    setResponse((prev) => ({
      ...prev,
      status: asyncStatus.loading
    }));

    const source = axios.CancelToken.source();
    const requestConfig = typeof callbackOrObj === 'function' ? callbackOrObj() : callbackOrObj;
    const axiosConfigOverride = typeof requestConfig === 'object'
    && Object.keys(requestConfig).length
      ? requestConfig
      : {};

    axios({
      ...axiosConfig,
      ...axiosConfigOverride,
      url,
      cancelToken: source.token,
    }).then((data) => {
      setResponse((prev) => ({
        ...prev,
        data,
        status: asyncStatus.success,
      }));
    }, (error) => {
      setResponse((prev) => ({
        ...prev,
        error,
        status: asyncStatus.rejected,
      }));
    });

    return () => {
      source.cancel();
    };
  }, [axiosConfig, url]);

  const execAsync = useCallback((callbackOrObj) => {
    if (typeof url === 'undefined') {
      return;
    }

    setResponse((prev) => ({
      ...prev,
      status: asyncStatus.loading
    }));

    const requestConfig = typeof callbackOrObj === 'function' ? callbackOrObj() : callbackOrObj;
    const axiosConfigOverride = typeof requestConfig === 'object'
    && Object.keys(requestConfig).length
      ? requestConfig
      : {};

    return new Promise((resolve, reject) => {
      axios({
        ...axiosConfig,
        ...axiosConfigOverride,
        url,
      }).then((data) => {
        setResponse((prev) => ({
          ...prev,
          data,
          status: asyncStatus.success,
        }));
        resolve(data);
      }, (error) => {
        setResponse((prev) => ({
          ...prev,
          error,
          status: asyncStatus.rejected,
        }));
        reject(error);
      });
    });
  }, [axiosConfig, url]);

  useEffect(() => {
    if (config.manual) {
      return;
    }

    const cancelRequest = exec();

    return () => {
      if (typeof cancelRequest === 'function' && config.cancelRequestOnUnmount) {
        cancelRequest();
      }
    };
  }, [config, exec, ...watchDeps]);

  return {
    ...response,
    exec,
    execAsync,
    isLoading: response.status === asyncStatus.loading,
    isIdle: response.status === asyncStatus.idle,
    isSuccess: response.status === asyncStatus.success,
    isError: response.status === asyncStatus.rejected,
  };
};

export default useAxios;
